import { useState } from "react";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import {
  Smartphone, LogIn, ArrowRight, X, Truck, Map, FileText, Banknote, ShieldCheck,
} from "lucide-react";

const APP_QR_URL =
  "https://app.emergent.sh/share-preview?app=exp%3A%2F%2Fcourier-nav-4.preview.emergentagent.com";

export default function Welcome() {
  const [showQR, setShowQR] = useState(false);

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="welcome-page">
      {/* ---------- Top bar ---------- */}
      <header className="border-b border-zinc-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/assets/gopossible-header.png"
              alt="GoPossible · Powered by Ambitions"
              className="h-12 w-auto"
              onError={(e) => {
                e.currentTarget.src = "/assets/gopossible-logo.png";
              }}
            />
          </div>
          <Link
            to="/login"
            data-testid="topbar-login-link"
            className="font-mono text-xs uppercase tracking-wider text-zinc-600 hover:text-brand"
          >
            Zaloguj się →
          </Link>
        </div>
      </header>

      {/* ---------- Hero ---------- */}
      <section className="max-w-6xl mx-auto px-6 pt-20 pb-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left: copy */}
          <div>
            <div className="font-label mb-4">// GOPOSSIBLE SP. Z O.O.</div>
            <h1 className="font-heading font-black text-5xl md:text-6xl uppercase tracking-tight leading-[0.95]">
              Trasy.<br />
              Księgowość.<br />
              <span className="text-brand">Obsługa floty.</span>
            </h1>
            <p className="mt-6 text-zinc-700 text-base leading-relaxed max-w-md">
              System zarządzania firmą kurierską — flota, dokumenty,
              finanse i appka kuriera w&nbsp;jednym miejscu.
            </p>

            {/* CTAs */}
            <div className="mt-10 grid sm:grid-cols-2 gap-4">
              <Link
                to="/login"
                data-testid="cta-login"
                className="group flex items-center justify-between gap-3 px-6 py-5 bg-zinc-900 text-white hover:bg-brand transition-colors border-2 border-zinc-900 hover:border-brand"
              >
                <div className="flex items-center gap-3">
                  <LogIn className="w-5 h-5" />
                  <div className="text-left">
                    <div className="font-mono text-[10px] uppercase tracking-wider opacity-70">
                      Pracownik biura
                    </div>
                    <div className="font-bold uppercase tracking-wide">Zaloguj się</div>
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>

              <button
                onClick={() => setShowQR(true)}
                data-testid="cta-download-app"
                className="group flex items-center justify-between gap-3 px-6 py-5 bg-white text-zinc-900 hover:bg-brand hover:text-white transition-colors border-2 border-zinc-900"
              >
                <div className="flex items-center gap-3">
                  <Smartphone className="w-5 h-5" />
                  <div className="text-left">
                    <div className="font-mono text-[10px] uppercase tracking-wider opacity-70">
                      Kurier
                    </div>
                    <div className="font-bold uppercase tracking-wide">Pobierz aplikację</div>
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>

          {/* Right: feature panel */}
          <div className="relative">
            <div className="border-2 border-zinc-900 bg-white p-8 shadow-[12px_12px_0_0_#18181b]">
              <div className="font-label mb-4">// MODUŁY</div>
              <div className="grid grid-cols-2 gap-4">
                <Feature icon={Truck} label="Flota & GPS" />
                <Feature icon={Map} label="Planer Tras" />
                <Feature icon={FileText} label="Umowy" />
                <Feature icon={Banknote} label="Finanse" />
                <Feature icon={Smartphone} label="Appka kuriera" />
                <Feature icon={ShieldCheck} label="RBAC" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- Footer ---------- */}
      <footer className="border-t border-zinc-200 mt-16">
        <div className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between text-xs font-mono text-zinc-500">
          <span>© 2026 GoPossible Sp. z o.o.</span>
          <span className="uppercase tracking-wider">Powered by Ambitions</span>
        </div>
      </footer>

      {/* ---------- QR modal ---------- */}
      {showQR && (
        <div
          className="fixed inset-0 bg-zinc-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowQR(false)}
          data-testid="qr-modal-backdrop"
        >
          <div
            className="bg-white border-2 border-zinc-900 max-w-md w-full p-8 relative shadow-[12px_12px_0_0_#18181b]"
            onClick={(e) => e.stopPropagation()}
            data-testid="qr-modal"
          >
            <button
              onClick={() => setShowQR(false)}
              data-testid="qr-modal-close"
              className="absolute top-3 right-3 p-2 hover:bg-zinc-100 transition-colors"
              aria-label="Zamknij"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="font-label mb-2">// Aplikacja kuriera</div>
            <h2 className="font-heading font-black text-2xl uppercase tracking-tight mb-1">
              Zeskanuj telefonem
            </h2>
            <p className="text-sm text-zinc-600 mb-6">
              Otwórz aparat lub czytnik QR i nakieruj na kod poniżej —
              instrukcja instalacji otworzy się w&nbsp;przeglądarce telefonu.
            </p>

            <div
              className="flex justify-center p-6 border-2 border-zinc-900 bg-white"
              data-testid="qr-code-container"
            >
              <QRCodeSVG
                value={APP_QR_URL}
                size={240}
                level="M"
                bgColor="#ffffff"
                fgColor="#18181B"
              />
            </div>

            <div className="mt-5 text-center">
              <a
                href={APP_QR_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="qr-fallback-link"
                className="font-mono text-xs uppercase tracking-wider text-brand hover:underline"
              >
                lub kliknij tutaj &rarr;
              </a>
            </div>

            <div className="mt-6 pt-5 border-t border-zinc-200 grid grid-cols-2 gap-3">
              <StoreBadge label="Google Play" sub="(wkrótce)" />
              <StoreBadge label="App Store" sub="(wkrótce)" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Feature({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-zinc-50 border border-zinc-200 hover:border-brand hover:bg-brand/5 transition-colors">
      <Icon className="w-4 h-4 text-brand shrink-0" />
      <span className="text-xs font-bold uppercase tracking-wide">{label}</span>
    </div>
  );
}

function StoreBadge({ label, sub }) {
  return (
    <div className="flex flex-col items-center justify-center gap-1 px-3 py-3 border border-zinc-300 bg-zinc-50 opacity-60">
      <span className="font-bold uppercase text-xs tracking-wider">{label}</span>
      <span className="font-mono text-[10px] text-zinc-500">{sub}</span>
    </div>
  );
}
